/**
 * Шаблоны документов (ТЗ FR-40 … FR-42).
 *
 * Юрист правит формулировки сам: скачивает файл, меняет текст в Word и
 * загружает обратно. Разработчик в этом не участвует — в этом весь смысл
 * вынесения текстов в шаблоны.
 *
 * Шаблон с ошибочной разметкой не принимается. Это сделано намеренно: иначе
 * ошибка вскроется на реальном деле, когда документ нужен немедленно.
 */

import { useEffect, useState } from 'react';
import type { DocumentKind } from '@bd/doc-engine';
import { api, ApiError } from '../../api.js';

const KINDS: Array<[kind: DocumentKind, title: string]> = [
  ['zayavlenie', 'Заявление'],
  ['opis-imushchestva', 'Опись имущества'],
  ['spisok-kreditorov', 'Список кредиторов'],
];

interface Version {
  id: string;
  version: number;
  uploadedAt: string;
  uploadedBy: string | null;
  isCurrent: boolean;
  comment: string | null;
}

interface CheckResult {
  ok: boolean;
  issues: Array<{ severity: 'error' | 'warning'; message: string }>;
  variables: string[];
  sections: string[];
}

export function TemplatesPage() {
  const [kind, setKind] = useState<DocumentKind>('zayavlenie');
  const [versions, setVersions] = useState<Version[]>([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<{ message: string; details: string[] } | null>(null);
  const [check, setCheck] = useState<{ fileName: string; result: CheckResult } | null>(null);

  const load = () => {
    api
      .templateVersions(kind)
      .then((r) => setVersions(r.versions))
      .catch((e) => setError({ message: (e as Error).message, details: [] }));
  };

  useEffect(load, [kind]);

  const fail = (e: unknown) => {
    const apiError = e as ApiError;
    setError({ message: apiError.message, details: apiError.details ?? [] });
  };

  const uploadTemplate = async (file: File) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await api.uploadTemplate(kind, file, comment);
      setMessage(
        result.warnings.length > 0
          ? `Загружена версия ${result.version}. Предупреждения: ${result.warnings.join('; ')}`
          : `Загружена версия ${result.version}. Новые дела формируются по ней.`,
      );
      setComment('');
      load();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const checkFile = async (file: File) => {
    setBusy(true);
    setError(null);
    setCheck(null);
    try {
      setCheck({ fileName: file.name, result: await api.checkTemplateFile(file) });
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const makeCurrent = async (version: number) => {
    setError(null);
    try {
      await api.setCurrentTemplate(kind, version);
      setMessage(`Действующей стала версия ${version}`);
      load();
    } catch (e) {
      fail(e);
    }
  };

  const errors = check?.result.issues.filter((i) => i.severity === 'error') ?? [];
  const warnings = check?.result.issues.filter((i) => i.severity === 'warning') ?? [];

  return (
    <section className="block">
      <h2>Шаблоны документов</h2>

      <div className="toolbar">
        {KINDS.map(([value, title]) => (
          <button
            key={value}
            type="button"
            className={kind === value ? 'tab tab-active' : 'tab'}
            onClick={() => {
              setKind(value);
              setCheck(null);
              setMessage(null);
              setError(null);
            }}
          >
            {title}
          </button>
        ))}
      </div>

      <p className="hint">
        Шаблон — обычный файл .docx, который правится в Word. Разметка описана в docs/template-syntax.md.
        Конструкции в фигурных скобках трогать нельзя, всё остальное — можно.
      </p>

      {message && <div className="success-box">{message}</div>}

      {error && (
        <div className="error-box">
          <strong>{error.message}</strong>
          {error.details.length > 0 && (
            <ul>
              {error.details.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <h3>Загрузить новую редакцию</h3>

      <label className="field">
        <span className="field-label">Что изменено и по чьему решению</span>
        <input
          value={comment}
          disabled={busy}
          placeholder="Например: уточнён абзац о доходах по решению от 12.09.2026"
          onChange={(e) => setComment(e.target.value)}
        />
      </label>

      <div className="toolbar">
        <label className="field field-small">
          <span className="field-label">Загрузить файл (.docx)</span>
          <input
            type="file"
            accept=".docx"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadTemplate(file);
              e.target.value = '';
            }}
          />
        </label>

        <label className="field field-small">
          <span className="field-label">Только проверить разметку</span>
          <input
            type="file"
            accept=".docx"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void checkFile(file);
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {busy && <p className="hint">Обработка…</p>}

      {check && (
        <div className={check.result.ok ? 'success-box' : 'error-box'}>
          <strong>
            {check.fileName}: {check.result.ok ? 'разметка верна' : 'разметка содержит ошибки'}
          </strong>

          {errors.length > 0 && (
            <ul>
              {errors.map((issue, i) => (
                <li key={i}>{issue.message}</li>
              ))}
            </ul>
          )}

          {warnings.length > 0 && (
            <>
              <p className="warning">Предупреждения:</p>
              <ul>
                {warnings.map((issue, i) => (
                  <li key={i}>{issue.message}</li>
                ))}
              </ul>
            </>
          )}

          {check.result.ok && (
            <p className="hint">
              Подстановок: {check.result.variables.length}, условных блоков и циклов: {check.result.sections.length}.
            </p>
          )}
        </div>
      )}

      <h3>Версии</h3>

      <table className="simple-table">
        <thead>
          <tr>
            <th>Версия</th>
            <th>Загружена</th>
            <th>Кем</th>
            <th>Комментарий</th>
            <th>Действующая</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <tr key={version.id}>
              <td>{version.version}</td>
              <td>{version.uploadedAt ? new Date(version.uploadedAt).toLocaleString('ru-RU') : '—'}</td>
              <td>{version.uploadedBy ?? '—'}</td>
              <td>{version.comment ?? '—'}</td>
              <td>{version.isCurrent ? 'да' : ''}</td>
              <td>
                {!version.isCurrent && (
                  <button type="button" className="link-button" onClick={() => makeCurrent(version.version)}>
                    Сделать действующей
                  </button>
                )}
              </td>
            </tr>
          ))}
          {versions.length === 0 && (
            <tr>
              <td colSpan={6} className="grid-empty">
                Шаблон ещё не загружен.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <p className="hint">
        Прежние версии не удаляются: ранее поданный документ должен воспроизводиться в той редакции,
        в какой был подан (FR-41).
      </p>
    </section>
  );
}
